import type { TelephonyConnectionView } from "@gioia365/contracts";
import { parseAllowedNumbers } from "./allowed-numbers";

// La bozza delle impostazioni della telefonia (piano M4, Task 14): registrazione, avviso al Lead e numeri ammessi, come si
// scrivono nel pannello, con la versione e i valori del server su cui la bozza è nata (`base`). Il salvataggio parte con la
// versione della base, mai con quella dell'ultima vista letta: se un collega ha salvato nel frattempo il server risponde
// `409 version_conflict` e non si sovrascrive niente. La vista che si rilegge (finestra tornata in primo piano, un'altra
// scrittura) non cambia da sola ciò che è scritto; `reconcile` dice che cosa farne.

/** Le impostazioni come il server le tiene, con la versione della riga. */
export type SettingsBase = Pick<TelephonyConnectionView, "version" | "recordingEnabled" | "recordingNotice" | "recordingRetentionDays" | "allowedDestinations">;

/** Le voci come si scrivono nei campi: l'avviso e i numeri sono testo. */
export type SettingsDraft = { recording: boolean; notice: string; allowed: string };

/** `conflicted`: il server ha già risposto `409` a questa bozza, e alla prossima lettura la bozza si rifonda sul nuovo. */
export type SettingsForm = { base: SettingsBase; draft: SettingsDraft; conflicted: boolean };

/** Le impostazioni di una vista (o di una risposta del server). */
export function settingsOf(view: SettingsBase): SettingsBase {
  return {
    version: view.version,
    recordingEnabled: view.recordingEnabled,
    recordingNotice: view.recordingNotice,
    recordingRetentionDays: view.recordingRetentionDays,
    allowedDestinations: view.allowedDestinations,
  };
}

/** La bozza di partenza: ciò che il server ha adesso. */
export function draftOf(base: SettingsBase): SettingsDraft {
  return { recording: base.recordingEnabled, notice: base.recordingNotice ?? "", allowed: (base.allowedDestinations ?? []).join("\n") };
}

export function startForm(view: SettingsBase): SettingsForm {
  const base = settingsOf(view);
  return { base, draft: draftOf(base), conflicted: false };
}

/** L'avviso scritto come lo salva il server: senza spazi ai bordi, vuoto = nessun avviso. */
export const noticeOf = (draft: SettingsDraft): string | null => (draft.notice.trim() === "" ? null : draft.notice.trim());

/** I numeri scritti come li salva il server: nessuno = nessun elenco; `undefined` se un numero non è valido. */
export function allowedOf(draft: SettingsDraft): string[] | null | undefined {
  const parsed = parseAllowedNumbers(draft.allowed);
  if (!parsed.ok) return undefined;
  return parsed.numbers.length === 0 ? null : parsed.numbers;
}

const sameList = (a: readonly string[] | null | undefined, b: readonly string[] | null | undefined) => JSON.stringify(a) === JSON.stringify(b);

/** Le voci su cui la bozza differisce dalla sua base. */
function touched(form: SettingsForm) {
  const { base, draft } = form;
  return {
    recording: draft.recording !== base.recordingEnabled,
    notice: noticeOf(draft) !== base.recordingNotice,
    allowed: !sameList(allowedOf(draft), base.allowedDestinations),
  };
}

/** C'è qualcosa di scritto che il server non ha ancora: se no, non c'è niente da salvare. */
export function isDirty(form: SettingsForm): boolean {
  const changes = touched(form);
  return changes.recording || changes.notice || changes.allowed;
}

/** Se due letture dicono le stesse impostazioni, anche se la versione è diversa (per esempio dopo una verifica del numero). */
function sameSettings(a: SettingsBase, b: SettingsBase): boolean {
  return (
    a.recordingEnabled === b.recordingEnabled &&
    a.recordingNotice === b.recordingNotice &&
    a.recordingRetentionDays === b.recordingRetentionDays &&
    sameList(a.allowedDestinations, b.allowedDestinations)
  );
}

/**
 * Che cosa fare quando la vista letta ha un'altra versione della base della bozza:
 * - le stesse impostazioni (è salito altro, per esempio la verifica del numero): la bozza resta, con la versione nuova;
 * - impostazioni diverse e niente di scritto: la bozza prende ciò che c'è, e non offre di salvare i valori vecchi;
 * - impostazioni diverse e qualcosa di scritto: la bozza resta sulla sua versione, e il salvataggio avrà il `409`;
 * - dopo quel `409`: la bozza si rifonda sul nuovo, tenendo ciò che era stato scritto e prendendo il resto da lì.
 * Restituisce lo stesso oggetto quando non cambia niente.
 */
export function reconcile(form: SettingsForm, view: SettingsBase): SettingsForm {
  if (view.version === form.base.version) return form;
  const base = settingsOf(view);
  if (sameSettings(base, form.base)) return { ...form, base, conflicted: false };
  if (!isDirty(form)) return startForm(base);
  if (!form.conflicted) return form;
  const changes = touched(form);
  const fresh = draftOf(base);
  return {
    base,
    conflicted: false,
    draft: {
      recording: changes.recording ? form.draft.recording : fresh.recording,
      notice: changes.notice ? form.draft.notice : fresh.notice,
      allowed: changes.allowed ? form.draft.allowed : fresh.allowed,
    },
  };
}
